import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  // Demo credentials
  const DEMO_CREDENTIALS = {
    email: 'demo@mealplanner.app',
    password: 'MealPlanner@123'
  };

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, enterGuestMode } = useAuth();
  const navigate = useNavigate();
  
  // Auto-fill demo credentials on component mount
  useEffect(() => {
    setEmail(DEMO_CREDENTIALS.email);
    setPassword(DEMO_CREDENTIALS.password);
  }, []);

  const handleGuestMode = () => {
    enterGuestMode();
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const loginData = await login(email, password);
        // Check if user has profile
        if (loginData.has_profile) {
          navigate('/');
        } else {
          navigate('/profile-setup');
        }
      } else {
        await register(email, password, name);
        // New users always need to complete profile
        navigate('/profile-setup');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, rgba(243, 234, 255, 0.8) 0%, rgba(255, 232, 240, 0.8) 25%, rgba(232, 244, 255, 0.8) 50%, rgba(255, 232, 213, 0.8) 75%, rgba(243, 234, 255, 0.8) 100%)' }}>
      <div className="flex items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Demo Account Banner */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-800">Demo Account</h3>
              <p className="text-sm text-blue-700 mt-1">
                Email: <span className="font-mono">{DEMO_CREDENTIALS.email}</span>
              </p>
              <p className="text-sm text-blue-700">
                Password: <span className="font-mono">{DEMO_CREDENTIALS.password}</span>
              </p>
              <p className="text-xs text-blue-600 mt-2">
                This is a demo account. Please change the password after login.
              </p>
            </div>
            
            <h1 className="heading-2 text-center mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Meal Planner</h1>
            <p className="body-medium text-center mb-8" style={{ color: 'rgb(112, 112, 112)' }}>Track your meals, count calories</p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="body-small text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                    required
                  />
                </div>
              )}

              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  required
                />
              </div>

              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Sign Up')}
              </button>
            </form>

            <div className="mt-6 text-center space-y-4">
              <div>
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="body-medium"
                  style={{ color: 'rgb(0, 128, 255)' }}
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
                </button>
              </div>
              <div className="text-xs text-gray-500">
                <p>This demo account will be available until {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
              <button
                onClick={handleGuestMode}
                className="w-full py-3 rounded-lg border-2 border-purple-600 text-purple-600 font-medium hover:bg-purple-50 transition-colors"
              >
                👤 Continue as Guest
              </button>
              <p className="body-small text-center mt-2" style={{ color: 'rgb(143, 143, 143)' }}>
                Try the AI meal planner without creating an account
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;